class PredictiveSearchNew extends PredictiveSearch {
  constructor() {
    super();
    this.sectionId = 'predictive-search-new';
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search-new');
    this.viewportPadding = 16;
    this.onViewportChange = this.onViewportChange.bind(this);
    this.predictiveSearchPanel = this.querySelector('[data-predictive-search]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search-results]');
    this.loadingOverlay = this.querySelector('.predictive-search-new__loading-overlay');
  }

  getPanelElement() {
    return this.predictiveSearchPanel || this.querySelector('[data-predictive-search]');
  }

  connectedCallback() {
    window.addEventListener('resize', this.onViewportChange);
    window.addEventListener('scroll', this.onViewportChange, true);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onViewportChange);
    window.removeEventListener('scroll', this.onViewportChange, true);
  }

  onViewportChange() {
    if (this.isOpen) this.clampToViewport();
  }

  getResultsMaxHeight() {
    if (this.isInDrawer()) {
      const container = this.closest('.menu-drawer__navigation-container');
      const field = this.querySelector('.search-bar__field');
      const panel = this.getPanelElement();

      if (container && field && panel) {
        return Math.max(
          container.getBoundingClientRect().bottom - field.getBoundingClientRect().bottom - this.viewportPadding,
          200
        );
      }
    }

    const panel = this.getPanelElement();
    if (!panel) return super.getResultsMaxHeight();

    const top = panel.getBoundingClientRect().top;
    return Math.max(window.innerHeight - top - this.viewportPadding, 200);
  }

  isInDrawer() {
    return Boolean(this.closest('#menu-drawer'));
  }

  open() {
    const panel = this.getPanelElement();
    panel.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.clampToViewport();
  }

  setLiveRegionLoadingState() {
    super.setLiveRegionLoadingState();

    if (this.loadingOverlay) {
      this.loadingOverlay.setAttribute('aria-hidden', 'false');
    }

    if (!this.isOpen) {
      this.open();
    }
  }

  clampToViewport() {
    const panel = this.getPanelElement();
    if (!panel) return;

    if (this.isInDrawer()) {
      this.clampToDrawer(panel);
      return;
    }

    const pad = this.viewportPadding;
    const maxWidth = Math.min(890, window.innerWidth - pad * 2);

    panel.style.width = `${maxWidth}px`;
    panel.style.maxWidth = `${maxWidth}px`;
    panel.style.left = '50%';
    panel.style.right = 'auto';
    panel.style.transform = 'translateX(-50%)';

    requestAnimationFrame(() => {
      const rect = panel.getBoundingClientRect();
      let shift = 0;

      if (rect.left < pad) {
        shift = pad - rect.left;
      } else if (rect.right > window.innerWidth - pad) {
        shift = window.innerWidth - pad - rect.right;
      }

      panel.style.transform = shift ? `translateX(calc(-50% + ${shift}px))` : 'translateX(-50%)';

      const maxHeight = Math.max(window.innerHeight - panel.getBoundingClientRect().top - pad, 200);
      panel.style.maxHeight = `${maxHeight}px`;

      const content = panel.querySelector('.snize-ac-results-content');
      if (content) {
        content.style.maxHeight = `${maxHeight}px`;
      }
    });
  }

  clampToDrawer(panel) {
    const pad = this.viewportPadding;

    panel.style.width = '100%';
    panel.style.maxWidth = '100%';
    panel.style.left = 'auto';
    panel.style.right = 'auto';
    panel.style.transform = 'none';

    requestAnimationFrame(() => {
      const container = this.closest('.menu-drawer__navigation-container');
      const field = this.querySelector('.search-bar__field');
      let maxHeight = Math.max(window.innerHeight - panel.getBoundingClientRect().top - pad, 200);

      if (container && field) {
        maxHeight = Math.max(
          container.getBoundingClientRect().bottom - field.getBoundingClientRect().bottom - pad,
          200
        );
      }

      panel.style.maxHeight = `${maxHeight}px`;

      const content = panel.querySelector('.snize-ac-results-content');
      if (content) {
        const panelPadding = 40;
        content.style.maxHeight = `${Math.max(maxHeight - panelPadding, 160)}px`;
      }
    });
  }

  closeResults(clearSearchTerm = false) {
    super.closeResults(clearSearchTerm);

    const panel = this.getPanelElement();
    if (!panel) return;

    panel.style.removeProperty('width');
    panel.style.removeProperty('max-width');
    panel.style.removeProperty('left');
    panel.style.removeProperty('right');
    panel.style.removeProperty('transform');
    panel.style.removeProperty('max-height');

    if (this.loadingOverlay) {
      this.loadingOverlay.setAttribute('aria-hidden', 'true');
    }

    const content = panel.querySelector('.snize-ac-results-content');
    if (content) content.style.removeProperty('max-height');
  }

  getEmptyStateMarkup() {
    const template = this.querySelector('[data-predictive-search-empty-template]');

    if (template?.innerHTML?.trim()) {
      return template.innerHTML;
    }

    const message = 'Start typing for search results';
    const productsLabel = this.getAttribute('data-products-label') || 'Products';

    return `
      <div id="predictive-search-results" class="snize-ac-results-content predictive-search-new__empty-state" role="listbox">
        <div class="snize-ac-results-columns">
          <div class="snize-ac-results-column">
            <ul class="snize-ac-results-multicolumn-list snize-ac-results-list-last predictive-search-new__empty-products">
              <li class="snize-label snize-label--full">${productsLabel}</li>
              <li class="predictive-search-new__placeholder">${message}</li>
            </ul>
          </div>
        </div>
      </div>
      <span class="hidden" data-predictive-search-live-region-count-value>${message}</span>
    `;
  }

  showEmptyState() {
    this.abortPendingSearch();

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML = this.getEmptyStateMarkup();
      this.animateResultsIn();
    }

    this.searchTerm = '';
    this.removeAttribute('results');
    this.removeAttribute('loading');

    if (this.loadingOverlay) {
      this.loadingOverlay.setAttribute('aria-hidden', 'true');
    }

    this.open();
  }

  abortPendingSearch() {
    this.abortController.abort();
    this.abortController = new AbortController();
  }

  safeUpdateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;

    if (!currentButtonText || !previousTerm) return;

    try {
      const escapedTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const matches = currentButtonText.match(new RegExp(escapedTerm, 'g'));

      if (matches && matches.length > 1) return;

      searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
    } catch (error) {
      return;
    }
  }

  renderSearchResults(resultsMarkup) {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.animateResultsIn();
    this.setAttribute('results', true);

    const liveRegion = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (liveRegion) {
      this.setLiveRegionResults();
    } else {
      this.removeAttribute('loading');
    }

    if (this.loadingOverlay) {
      this.loadingOverlay.setAttribute('aria-hidden', 'true');
    }

    this.open();
  }

  animateResultsIn() {
    if (!this.predictiveSearchResults) return;

    this.predictiveSearchResults.classList.remove('predictive-search-new__results--visible');
    void this.predictiveSearchResults.offsetWidth;
    this.predictiveSearchResults.classList.add('predictive-search-new__results--visible');
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) {
      this.showEmptyState();
      return;
    }

    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onChange() {
    SearchForm.prototype.onChange.call(this);

    const newSearchTerm = this.getQuery();
    const previousSearchTerm = this.searchTerm;

    if (!previousSearchTerm || !newSearchTerm.startsWith(previousSearchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    this.safeUpdateSearchForTerm(previousSearchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.showEmptyState();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.showEmptyState();
      return;
    }

    switch (event.code) {
      case 'ArrowUp':
        event.preventDefault();
        this.switchOption('up');
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.switchOption('down');
        break;
      case 'Enter':
        event.preventDefault();
        this.selectOption();
        break;
    }
  }

  onFormReset(event) {
    event.preventDefault();

    if (!this.shouldResetForm()) return;

    this.input.value = '';
    SearchForm.prototype.onChange.call(this);
    this.searchTerm = '';
    this.abortPendingSearch();

    if (document.activeElement === this.input) {
      this.showEmptyState();
    } else {
      this.closeResults(true);
    }
  }

  getSearchResults(searchTerm) {
    this.abortPendingSearch();

    const queryKey = searchTerm.replace(/\s+/g, '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      if (searchTerm !== this.getQuery()) return;
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    const activeQuery = searchTerm;

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=${this.sectionId}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        if (activeQuery !== this.getQuery()) {
          this.removeAttribute('loading');
          if (this.loadingOverlay) {
            this.loadingOverlay.setAttribute('aria-hidden', 'true');
          }
          return;
        }

        const section = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector(`#shopify-section-${this.sectionId}`);

        if (!section || !section.innerHTML.trim()) {
          this.removeAttribute('loading');
          if (this.loadingOverlay) {
            this.loadingOverlay.setAttribute('aria-hidden', 'true');
          }
          return;
        }

        const resultsMarkup = section.innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(
      this.querySelectorAll('li[role="option"], button.snize-view-all-link')
    ).filter((element) => element.offsetParent !== null);

    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, [aria-selected="true"].snize-view-all-link, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }
}

customElements.define('predictive-search-new', PredictiveSearchNew);

function initMobileSearchTrigger() {
  document.querySelectorAll('[data-mobile-search-trigger]').forEach((btn) => {
    if (btn.dataset.mobileSearchBound) return;
    btn.dataset.mobileSearchBound = 'true';

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const details = document.getElementById('Details-menu-drawer-container');
      const summary = details?.querySelector('summary');

      if (details && !details.hasAttribute('open')) {
        summary?.click();
      }

      setTimeout(() => {
        const drawerInput = document.getElementById('Search-In-Drawer');
        drawerInput?.focus();

        const drawerSearch = drawerInput?.closest('predictive-search-new');
        if (drawerSearch?.isOpen) {
          drawerSearch.clampToViewport();
        }
      }, 350);
    });
  });
}

function initDrawerSearchClamp() {
  const details = document.getElementById('Details-menu-drawer-container');
  if (!details || details.dataset.searchClampBound) return;
  details.dataset.searchClampBound = 'true';

  details.addEventListener('toggle', () => {
    if (!details.hasAttribute('open')) return;

    requestAnimationFrame(() => {
      document.querySelectorAll('#menu-drawer predictive-search-new').forEach((instance) => {
        if (instance.isOpen) instance.clampToViewport();
      });
    });
  });
}

function initHeaderSearchMobile() {
  initMobileSearchTrigger();
  initDrawerSearchClamp();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHeaderSearchMobile);
} else {
  initHeaderSearchMobile();
}
